/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Models */
import { DsLinearWorkFormTemplateLink } from '../../models/ds-linear-work-form-template-link';
/** Interfaces */
import { DsLinearWorkFormTemplateInterface } from '../../interfaces/ds-linear-work-form-template.interface';

@Injectable({
  providedIn: 'root',
})
/**
 * Service for organisation linear work forms
 */
export class DsOrganisationLinearWorkModelFormService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all form models linked to the selected linear work model.
   *
   * @param organisationId Organisation ID
   * @param workId Id of the linear work model
   * @param elementsPerPage Elements per page
   * @param pageIndex Page index
   * @returns Observable type `<BydModel<DsLinearWorkModel>>`
   */
  public getLinearWorkModelForms(
    organisationId: number,
    workId: number,
    elementsPerPage = 10,
    pageIndex = 0
  ): Observable<BydModel<DsLinearWorkFormTemplateLink>> {
    this.path = `/v1/organisations/${organisationId}/works/${workId}/forms`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    return this.getModels<DsLinearWorkFormTemplateLink>({ headers });
  }

  /**
   * Add a link between a linear work model and a form model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model
   * @param formModelId Id of the form model
   * @returns Observable<boolean>
   */
  public linkLinearWorkModelToForm(
    organisationId: number,
    linearWorkModelId: number,
    formModelId: number
  ): Observable<boolean> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}/forms/${formModelId}`;
    return this.post<boolean>();
  }

  /**
   * Deletes the link between a linear work model and a form model.
   *
   * @param organisationId Organisation ID
   * @param linearWorkModelId Id of the linear work model
   * @param formModelId Id of the form model
   * @returns Observable<boolean>
   */
  public deleteLinearWorkModelFormLink(
    organisationId: number,
    linearWorkModelId: number,
    formModelId: number
  ): Observable<boolean> {
    this.path = `/v1/organisations/${organisationId}/works/${linearWorkModelId}/forms/${formModelId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsLinearWorkFormTemplateLink instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsLinearWorkFormTemplateLink` instance.
   */
  public createNewInstance(jsonObject: DsLinearWorkFormTemplateInterface): DsLinearWorkFormTemplateLink {
    return new DsLinearWorkFormTemplateLink(jsonObject);
  }
}
